/**
 * MA-PPB-FUNNEL-SKELETON-CORRECTION-087: Valid Initial State Skeleton
 * 
 * Matches the EXACT three-section structure of ConversationFunnel INITIAL STATE:
 * 1. Header: Icon (32×32px, left) + Title placeholder (right, gap 10px)
 * 2. Message Thread: 2 agent-only message bubbles (NO user messages yet)
 * 3. Response Area: 2 reply chip placeholders (right-aligned, 8px gap)
 * 
 * Critical Design Rules:
 * - Header MUST include icon placeholder (prevents layout shift on icon load)
 * - Message thread MUST be agent-only (NO user bubbles — user hasn't interacted yet)
 * - Skeleton represents "initial state before user interaction"
 * - Uses LP tokens for seamless skeleton → real UI transition
 * - No layout shift when content loads
 */

import React from 'react';
import { Loader2 } from 'lucide-react';

export default function FunnelSkeleton({ lpTokens, isLpDark = false, height = 600 }) {
  // MA-PPB-FUNNEL-SKELETON-STRUCTURE-086: Extract tokens to match real funnel colors
  const tokens = lpTokens || {
    cardBg: isLpDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
    threadBg: isLpDark ? 'rgba(255,255,255,0.02)' : '#f8fafc',
    textPrimary: isLpDark ? '#E5E5E5' : '#1F2937',
    textMuted: isLpDark ? '#9CA3AF' : '#6B7280',
    borderSubtle: isLpDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    accentSoft: isLpDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.1)',
    accent: '#6366F1',
    bubbleAgentBg: isLpDark ? 'rgba(255,255,255,0.06)' : '#E9E9EB',
    bubbleUserBg: isLpDark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.12)',
  };

  // MA-PPB-FUNNEL-SKELETON-STRUCTURE-086: Layout constants from ConversationFunnel.jsx
  const HEADER_HEIGHT = 56;
  const RESPONSE_AREA_HEIGHT = 96;
  
  // MA-PPB-FUNNEL-SKELETON-STRUCTURE-086: Design system constants from ConversationFunnel DS object
  const DS = {
    space: { md: 12, lg: 16 },
    option: {
      height: 36,
      paddingX: 16,
      paddingY: 8,
      radius: '16px',
      radiusTail: '6px',
      gap: 8,
      maxWidth: '88%',
    },
  };

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '440px',
        height: `${height}px`,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        background: tokens.cardBg,
        borderRadius: '20px',
        boxShadow: isLpDark 
          ? '0 8px 24px rgba(0,0,0,0.4)' 
          : '0 8px 24px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      {/* ============================================================ */}
      {/* SECTION 1: HEADER — Icon + Title (matches FunnelHeader exactly) */}
      {/* ============================================================ */}
      <div
        data-section="header"
        style={{
          height: `${HEADER_HEIGHT}px`,
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          borderBottom: `1px solid ${tokens.borderSubtle}`,
          flexShrink: 0,
          backgroundColor: tokens.cardBg,
        }}
      >
        {/* Icon Placeholder — 32x32px, borderRadius 8px (matches real header icon) */}
        {/* CRITICAL: This must be visible - using solid accent color */}
        <div
          data-element="header-icon-placeholder"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: tokens.accent || '#6366F1',
            opacity: 0.2,
            flexShrink: 0,
          }}
        />
        
        {/* Title Placeholder — positioned next to icon (matches real header title) */}
        <div
          data-element="header-title-placeholder"
          style={{
            height: '16px',
            width: '140px',
            borderRadius: '4px',
            background: isLpDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
          }}
        />
      </div>

      {/* ============================================================ */}
      {/* SECTION 2: MESSAGE THREAD — Agent-only messages */}
      {/* ============================================================ */}
      <div
        data-section="message-thread"
        style={{
          flex: 1,
          padding: `20px ${DS.space.lg}px`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          gap: `${DS.space.md}px`,
          overflowY: 'hidden',
          backgroundColor: tokens.threadBg || tokens.cardBg,
        }}
      >
        {/* Agent Message Bubble — Left-aligned */}
        <div
          data-element="agent-message-bubble"
          className="flex justify-start"
        >
          <div
            style={{
              maxWidth: '85%',
              padding: '8px 16px',
              borderRadius: '16px 16px 16px 4px',
              background: tokens.bubbleAgentBg,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              minHeight: '36px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            {/* Text line placeholders (2 lines for realistic question length) */}
            <div style={{
              height: '14px',
              width: '200px',
              borderRadius: '3px',
              background: isLpDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
            }} />
            <div style={{
              height: '14px',
              width: '160px',
              borderRadius: '3px',
              background: isLpDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
            }} />
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/* SECTION 3: RESPONSE AREA — User message bubble placeholders (right-aligned) */}
      {/* ============================================================ */}
      <div
        data-section="response-area"
        style={{
          height: `${RESPONSE_AREA_HEIGHT}px`,
          padding: `12px ${DS.space.lg}px`,
          borderTop: `1px solid ${tokens.borderSubtle}`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-end',
          gap: `${DS.space.md}px`,
          flexShrink: 0,
          backgroundColor: tokens.cardBg,
        }}
      >
        {/* User Message Bubble Placeholder 1 — Matches UserMessage component */}
        <div
          data-element="user-response-bubble"
          className="flex justify-end"
        >
          <div
            style={{
              maxWidth: '85%',
              padding: '8px 16px',
              borderRadius: '16px 16px 4px 16px',
              background: tokens.bubbleUserBg || tokens.accentSoft,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              minHeight: '36px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {/* Text line placeholder */}
            <div style={{
              height: '14px',
              width: '100px',
              borderRadius: '3px',
              background: isLpDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
            }} />
          </div>
        </div>

        {/* User Message Bubble Placeholder 2 */}
        <div
          data-element="user-response-bubble"
          className="flex justify-end"
        >
          <div
            style={{
              maxWidth: '85%',
              padding: '8px 16px',
              borderRadius: '16px 16px 4px 16px',
              background: tokens.bubbleUserBg || tokens.accentSoft,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              minHeight: '36px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {/* Text line placeholder */}
            <div style={{
              height: '14px',
              width: '80px',
              borderRadius: '3px',
              background: isLpDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
            }} />
          </div>
        </div>
      </div>
    </div>
  );
}
